import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, InputAdornment, MenuItem, Paper, Snackbar, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Add, CheckCircle, Cancel, Download, Refresh, Send } from '@mui/icons-material';
import { gymInvoicesAPI } from '../services/api';
import { useAppData } from '../context/AppDataContext';
import { CURRENCY_CODE, CURRENCY_SYMBOL, formatCurrency } from '../utils/currency';
import { createPdf, pdfLine, pdfRect, pdfText, wrapPdfText } from '../utils/pdf';

const emptyForm = {
  gymId: '',
  title: '',
  amount: '',
  dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  description: '',
};

function statusChip(status) {
  const map = {
    sent: { label: 'Sent', bg: '#DBEAFE', color: '#1D4ED8' },
    paid: { label: 'Paid', bg: '#DCFCE7', color: '#16A34A' },
    overdue: { label: 'Overdue', bg: '#FEE2E2', color: '#B91C1C' },
    cancelled: { label: 'Cancelled', bg: '#E5E7EB', color: 'text.primary' },
  };
  const cfg = map[status] || map.sent;
  return <Chip label={cfg.label} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: 11 }} />;
}

function Stat({ label, value }) {
  return (
    <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase' }}>{label}</Typography>
      <Typography sx={{ mt: 0.5, fontSize: 24, fontWeight: 800 }}>{value}</Typography>
    </Paper>
  );
}

function moneyForPdf(value) {
  return `${CURRENCY_CODE} ${Number(value || 0).toLocaleString('en-IN')}`;
}

export default function GymInvoices() {
  const { gyms } = useAppData();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        gymInvoicesAPI.getAll(),
        gymInvoicesAPI.getStats(),
      ]);
      setInvoices(listRes.data.data || []);
      setStats(statsRes.data.data || null);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.gymId || !form.title || !form.amount || !form.dueDate) {
      setError('Gym, title, amount, and due date are required');
      return;
    }

    try {
      await gymInvoicesAPI.create({ ...form, amount: Number(form.amount) });
      setSnackbar({ open: true, message: 'Gym invoice sent by email' });
      setOpen(false);
      setForm(emptyForm);
      setError('');
      loadAll();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to create gym invoice');
    }
  };

  const handleGymChange = (gymId) => {
    const selectedGym = gyms.find(gym => gym._id === gymId);
    const defaultAmount = selectedGym?.onboardingPricing?.finalMonthlyCost;

    setForm(f => ({
      ...f,
      gymId,
      amount: Number.isFinite(Number(defaultAmount)) ? String(defaultAmount) : f.amount,
    }));
  };

  const markPaid = async (id) => {
    await gymInvoicesAPI.markPaid(id);
    setSnackbar({ open: true, message: 'Invoice marked paid' });
    loadAll();
  };

  const cancelInvoice = async (id) => {
    await gymInvoicesAPI.cancel(id);
    setSnackbar({ open: true, message: 'Invoice cancelled' });
    loadAll();
  };

  const downloadInvoicePdf = (invoice) => {
    const isPaid = invoice.status === 'paid';
    const docType = isPaid ? 'RECEIPT' : 'INVOICE';
    const invoiceId = String(invoice.invoiceNumber || invoice._id || '').slice(-8).toUpperCase();
    const gymName = invoice.gymId?.name || 'Gym';
    const gymEmail = invoice.gymId?.email || '-';
    const invoiceTitle = invoice.title || 'Platform Fee';
    const amount = Number(invoice.amount || 0);
    const paidAmount = Number(invoice.amountPaid || 0);
    const balance = Number(invoice.outstandingAmount || 0);
    const dueDateStr = invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('en-IN') : '-';
    const dateStr = invoice.createdAt ? new Date(invoice.createdAt).toLocaleDateString('en-IN') : '-';
    const sentTo = Array.isArray(invoice.sentTo) && invoice.sentTo.length ? invoice.sentTo.join(', ') : '-';
    const filename = `${invoice.invoiceNumber || 'gym-invoice'}.pdf`;

    const statusMap = {
      sent: 'Sent', paid: 'Paid', overdue: 'Overdue', cancelled: 'Cancelled'
    };
    const statusLabel = statusMap[invoice.status] || 'Sent';

    createPdf(filename, () => {
      const c = [];
      const pageW = 595;
      const left = 48;
      const right = 547;
      const tableTop = 505;
      const rowH = 34;
  
      c.push(pdfText(docType, pageW / 2, 796, { size: 24, bold: true, align: 'center' }));
      c.push(pdfText('Fitistic Platform Billing', pageW / 2, 776, { size: 11, align: 'center', color: '0.34 0.40 0.48' }));
      c.push(pdfLine(left, 756, right, 756, '0.10 0.31 0.65', 1.5));
  
      c.push(pdfText('Bill To', left, 728, { size: 9, bold: true, color: '0.34 0.40 0.48' }));
      c.push(pdfText(gymName, left, 710, { size: 13, bold: true }));
      c.push(pdfText(gymEmail, left, 694, { size: 10, color: '0.34 0.40 0.48' }));
      c.push(pdfText(`Emails Sent To: ${sentTo}`, left, 678, { size: 10, color: '0.34 0.40 0.48' }));
  
      c.push(pdfText(`Invoice No.`, 365, 728, { size: 9, bold: true, color: '0.34 0.40 0.48' }));
      c.push(pdfText(invoiceId, right, 728, { size: 10, bold: true, align: 'right' }));
      c.push(pdfText(`Date`, 365, 710, { size: 9, color: '0.34 0.40 0.48' }));
      c.push(pdfText(dateStr, right, 710, { size: 10, align: 'right' }));
      c.push(pdfText('Due Date', 365, 692, { size: 9, color: '0.34 0.40 0.48' }));
      c.push(pdfText(dueDateStr, right, 692, { size: 10, align: 'right' }));
      c.push(pdfText('Status', 365, 674, { size: 9, color: '0.34 0.40 0.48' }));
      c.push(pdfText(statusLabel, right, 674, { size: 10, bold: true, align: 'right' }));
  
      c.push(pdfRect(left, 602, 499, 52, { stroke: '0.85 0.87 0.91', fill: '0.97 0.98 0.99' }));
      c.push(pdfText('Platform Details', left + 14, 633, { size: 9, bold: true, color: '0.34 0.40 0.48' }));
      c.push(pdfText('Fitistic Technologies', left + 14, 616, { size: 12, bold: true }));
      c.push(pdfText('support@fitistic.com', 310, 616, { size: 10, color: '0.34 0.40 0.48' }));
  
      c.push(pdfText('Charges', left, 564, { size: 11, bold: true }));
      c.push(pdfText(`Type: Platform Subscription`, left, 546, { size: 10, color: '0.34 0.40 0.48' }));
  
      c.push(pdfRect(left, tableTop - rowH, 499, rowH, { stroke: '0.85 0.87 0.91', fill: '0.95 0.97 1' }));
      c.push(pdfText('#', left + 12, tableTop - 22, { size: 9, bold: true, color: '0.34 0.40 0.48' }));
      c.push(pdfText('Description', left + 42, tableTop - 22, { size: 9, bold: true, color: '0.34 0.40 0.48' }));
      c.push(pdfText('Qty', 360, tableTop - 22, { size: 9, bold: true, align: 'right', color: '0.34 0.40 0.48' }));
      c.push(pdfText('Rate', 446, tableTop - 22, { size: 9, bold: true, align: 'right', color: '0.34 0.40 0.48' }));
      c.push(pdfText('Amount', 532, tableTop - 22, { size: 9, bold: true, align: 'right', color: '0.34 0.40 0.48' }));
  
      c.push(pdfRect(left, tableTop - rowH * 2, 499, rowH, { stroke: '0.85 0.87 0.91' }));
      c.push(pdfText('1', left + 12, tableTop - 56, { size: 10 }));
      c.push(pdfText(invoiceTitle, left + 42, tableTop - 52, { size: 10, bold: true }));
      c.push(pdfText(invoice.description || 'Monthly billing', left + 42, tableTop - 66, { size: 8, color: '0.34 0.40 0.48' }));
      c.push(pdfText('1', 360, tableTop - 56, { size: 10, align: 'right' }));
      c.push(pdfText(moneyForPdf(amount), 446, tableTop - 56, { size: 10, align: 'right' }));
      c.push(pdfText(moneyForPdf(amount), 532, tableTop - 56, { size: 10, align: 'right' }));
  
      c.push(pdfLine(78, tableTop - rowH * 2, 78, tableTop, '0.85 0.87 0.91'));
      c.push(pdfLine(372, tableTop - rowH * 2, 372, tableTop, '0.85 0.87 0.91'));
      c.push(pdfLine(458, tableTop - rowH * 2, 458, tableTop, '0.85 0.87 0.91'));
  
      const totalsX = 345;
      const totalsY = 374;
      c.push(pdfText('Subtotal', totalsX, totalsY, { size: 10, color: '0.34 0.40 0.48' }));
      c.push(pdfText(moneyForPdf(amount), right, totalsY, { size: 10, align: 'right' }));
      c.push(pdfText('Amount Paid', totalsX, totalsY - 20, { size: 10, color: '0.34 0.40 0.48' }));
      c.push(pdfText(moneyForPdf(paidAmount), right, totalsY - 20, { size: 10, align: 'right' }));
      
      c.push(pdfLine(totalsX, totalsY - 32, right, totalsY - 32));
      c.push(pdfText('Balance Due', totalsX, totalsY - 54, { size: 12, bold: true }));
      c.push(pdfText(moneyForPdf(balance), right, totalsY - 54, { size: 12, bold: true, align: 'right' }));
  
      const noteMaxWidth = totalsX - left - 24;
      const footerNote = 'Please contact Fitistic support after completing payment so this invoice can be marked as paid.';
      const noteLines = wrapPdfText(footerNote, noteMaxWidth, 10).slice(0, 4);
      noteLines.forEach((nLine, i) => {
        c.push(pdfText(nLine, left, totalsY - (i * 14), { size: 10, color: '0.34 0.40 0.48' }));
      });
  
      return c;
    });
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Gym Invoices</Typography>
          <Typography variant="body2" color="text.secondary">
            Raise monthly invoices to gym admins, email outstanding dues, and download transaction PDFs.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadAll} disabled={loading}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}>Raise Invoice</Button>
        </Stack>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}><Stat label="Total Raised" value={formatCurrency(stats?.total || 0)} /></Grid>
        <Grid item xs={12} sm={6} md={3}><Stat label="Outstanding" value={formatCurrency(stats?.outstanding || 0)} /></Grid>
        <Grid item xs={12} sm={6} md={3}><Stat label="Paid" value={formatCurrency(stats?.paid || 0)} /></Grid>
        <Grid item xs={12} sm={6} md={3}><Stat label="Invoices" value={stats?.count || 0} /></Grid>
      </Grid>

      <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: 'background.paper' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>Invoice</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Gym</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Due Date</TableCell>
                <TableCell sx={{ fontWeight: 800 }} align="right">Amount</TableCell>
                <TableCell sx={{ fontWeight: 800 }} align="right">Outstanding</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 800 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map(invoice => (
                <TableRow key={invoice._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={800}>{invoice.invoiceNumber}</Typography>
                    <Typography variant="caption" color="text.secondary">{invoice.title}</Typography>
                  </TableCell>
                  <TableCell>{invoice.gymId?.name || '-'}</TableCell>
                  <TableCell>{new Date(invoice.dueDate).toLocaleDateString('en-IN')}</TableCell>
                  <TableCell align="right">{formatCurrency(invoice.amount)}</TableCell>
                  <TableCell align="right">{formatCurrency(invoice.outstandingAmount)}</TableCell>
                  <TableCell>{statusChip(invoice.status)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                        <Button size="small" startIcon={<CheckCircle />} color="success" onClick={() => markPaid(invoice._id)}>Paid</Button>
                      )}
                      {invoice.status !== 'cancelled' && invoice.status !== 'paid' && (
                        <Button size="small" startIcon={<Cancel />} color="error" onClick={() => cancelInvoice(invoice._id)}>Cancel</Button>
                      )}
                      <Button size="small" startIcon={<Download />} onClick={() => downloadInvoicePdf(invoice)}>PDF</Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {invoices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                    No gym invoices yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Raise Gym Invoice</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField select label="Gym" value={form.gymId} onChange={e => handleGymChange(e.target.value)} fullWidth>
              {gyms.filter(gym => !gym.isDefault).map(gym => (
                <MenuItem key={gym._id} value={gym._id}>{gym.name}</MenuItem>
              ))}
            </TextField>
            <TextField label="Invoice Title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} fullWidth />
            <TextField
              label="Amount"
              type="number"
              value={form.amount}
              onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              InputProps={{ startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment> }}
              fullWidth
            />
            <TextField label="Due Date" type="date" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField label="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} multiline rows={3} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<Send />} onClick={handleCreate}>Send Email Invoice</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} message={snackbar.message} />
    </Box>
  );
}
